import React from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { store } from "store";
import Tree from "react-d3-tree";
import Strings from "utils/strings";
import { setLoader, setTitle } from "store/actions";
import { API, Endpoints } from "utils/api";
import { push } from "connected-react-router";
import { Popover, notification, Input } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { getSuccessLevelBadge, getSuccessLevelBadgeColor, getTreeColor } from "utils/utils";
import "./styles.scss";

const { Search } = Input;

import logo from "assets/images/logo_horizontal_white.png";
import placeholder from "assets/images/placeholders/user.jpg";
import strings from "utils/strings";
import { CountryType } from "utils/types";

interface IProps {
	language: string;
	dispatch: typeof store.dispatch;
	partners: boolean;
	user: any;
	countries: CountryType[];
}

interface Partner {
	name: string;
	attributes?: {
		level: string;
	};
	children?: Partner[];
}

interface IState {
	partners: Partner[];
	withSearch?: boolean;
	searchName: string;
	searchEmail: string;
}

class NodeLabel extends React.PureComponent<any> {
	renderIndividualInfo() {
		const { nodeData, countries } = this.props;

		const { background, color } = getSuccessLevelBadgeColor(nodeData.successLevel);

		const countryCallingCode = nodeData.attributes?.phone?.substring(0, 4).replace("+", "");
		let country;

		if (countryCallingCode) country = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCode))?.alpha2Code;

		if (country) {
			nodeData.attributes.country = country.toLowerCase();
		}

		return (
			<div className="NodePartnerInfo" onClick={(e) => e.stopPropagation()}>
				<div className="PartnerCardHeader">
					<div className="PartnerCardHeaderInfo">
						<img src={nodeData.attributes?.photo || placeholder} alt={nodeData.name} />
						<h2>
							{nodeData.attributes?.country ? (
								<div className="react-tel-input">
									<div className="selected-flag">
										<span className={`flag ${nodeData.attributes?.country}`} />
									</div>
								</div>
							) : null}
							{nodeData.name}
							{Boolean(nodeData.attributes?.userCode) ? ` (#${nodeData.attributes.userCode})` : ""}
						</h2>
						<h4>{nodeData.attributes?.email}</h4>
						{nodeData.successLevel && (
							<div className="PartnerSuccessLevel">
								<div className="PartnerSuccessLevelBadge">
									<span style={{ background, color }}>{getSuccessLevelBadge(nodeData.successLevel)}</span>
								</div>
							</div>
						)}
					</div>
					<div className="PartnerCardHeaderAttributes">
						{Boolean(nodeData.referredBy) && (
							<h4 style={{ marginBottom: 5 }}>
								<strong>{Strings.users.referredBy}</strong>
							</h4>
						)}
						{Boolean(nodeData.referredBy) && <h5>{nodeData.referredBy?.name}</h5>}
						{Boolean(nodeData.referredBy) && <h5>{nodeData.referredBy?.email}</h5>}
					</div>
				</div>
				{!nodeData.noInfo && (
					<div className="PartnerCardData">
						<div className="PartnerCardDataItem">
							<span>{strings.users.personalPurchases}</span>
							<span>{nodeData.points?.cp || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.personalVolume}</span>
							<span>{nodeData.points?.juniorPv || nodeData.points?.pv || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.personalVolumeTotal}</span>
							<span>{nodeData.points?.tpv || 0}</span>
						</div>
						{/* <div className="PartnerCardDataItem">
							<span>{strings.users.personalVolumeAccumulated}</span>
							<span>{nodeData.points?.accumulatedTpv || 0}</span>
						</div> */}
						<div className="PartnerCardDataItem">
							<span>{strings.users.leftGroupVolume}</span>
							<span>{nodeData.points?.leftSgv || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.rightGroupVolume}</span>
							<span>{nodeData.points?.rightSgv || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.leftSpillover}</span>
							<span>{nodeData.points?.leftSpillover || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.rightSpillover}</span>
							<span>{nodeData.points?.rightSpillover || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.leftTotalGroupVolume}</span>
							<span>{nodeData.points?.leftTgv || 0}</span>
						</div>
						<div className="PartnerCardDataItem">
							<span>{strings.users.rightTotalGroupVolume}</span>
							<span>{nodeData.points?.rightTgv || 0}</span>
						</div>
						{
							//TODO: removed this by client request
							/*<div className="PartnerCardDataItem">
							<span>{strings.users.allowedTgv}</span>
							<span>{nodeData.points?.tgv || 0}</span>
						</div>*/
						}
					</div>
				)}
			</div>
		);
	}

	render() {
		const { className, nodeData, partners, searched } = this.props;
		const showLogo = nodeData.depth === 0 && !partners && !searched;
		const image = showLogo ? logo : nodeData.attributes?.photo || placeholder;
		const dispatch = store.dispatch;

		if (!nodeData.attributes?._id && !nodeData.noInfo) {
			return (
				<div className={className}>
					<div
						className="NodeObject NodeObjectDisable"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
					>
						<div className="NodeImage">
							<img src={image} alt={nodeData.name} />
						</div>
					</div>
				</div>
			);
		}

		const colors = getTreeColor(nodeData.successLevel);
		const badgeStyle = { background: colors.background, color: colors.color };

		const content = (
			<div
				className="NodeObject"
				onClick={(e) => {
					if (nodeData.depth > 0) {
						e.preventDefault();
						e.stopPropagation();

						delete nodeData.parent?.parent;

						if (partners) dispatch(push(`/partner-partners/${nodeData.attributes._id}`));
						else dispatch(push(`/partners/${nodeData.attributes._id}`));
					}
				}}
			>
				{showLogo ? (
					<div className="NodeImageBlock">
						<div className="NodeImage">
							<img src={image} alt={nodeData.name} />
						</div>
					</div>
				) : (
					<div className="NodeImage">
						<img src={image} alt={nodeData.name} />
					</div>
				)}
				{(partners || nodeData.depth !== 0 || searched) && nodeData.name && (
					<div className="NodeInfo">
						<span style={badgeStyle}>{nodeData.name}</span>
					</div>
				)}
			</div>
		);

		return (
			<div className={className}>
				{nodeData.depth > 0 ? (
					<Popover content={this.renderIndividualInfo()} trigger="hover" placement="bottom">
						{content}
					</Popover>
				) : (
					content
				)}
				<button className={`AdditionalNodes${nodeData._children?.length > 0 ? " --visible" : ""}`}>
					<div className="AdditionalNodeIcon">
						{nodeData._collapsed ? <ArrowDownOutlined translate={null} /> : <ArrowUpOutlined translate={null} />}
					</div>
				</button>
			</div>
		);
	}
}

class PartnersTree extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			partners: [
				{
					name: "Essencia da Vida",
					children: []
				}
			],
			searchName: "",
			searchEmail: ""
		};
	}

	componentDidMount(): void {
		const { dispatch } = this.props;

		dispatch(setTitle(Strings.users.partnerTree));

		this.getData();
	}

	componentWillUnmount(): void {
		const { dispatch } = this.props;
		dispatch(setTitle(""));
	}

	async getData(withSearch = false) {
		const { searchName, searchEmail } = this.state;
		const { dispatch, user } = this.props;

		dispatch(setLoader(true));

		const params: { name?: string; email?: string } = {};
		if (withSearch && searchName) params.name = searchName;
		if (withSearch && searchEmail) params.email = searchEmail;

		try {
			const response = await API.get({
				url: Endpoints.uriPartners("tree"),
				params,
				timeout: 0
			});

			if (response?.ok) {
				const { tree } = response.data.results || {};
				if (user.role === "sysadmin" || user.role === "admin") {
					const partners = [
						{
							name: "Essencia da Vida",
							children: tree,
							noInfo: true
						}
					];
					this.setState({ partners });
				} else if ((user.role === "sysadmin" || user.role === "admin") && withSearch && (searchName || searchEmail)) {
					this.setState({ partners: tree });
				} else {
					this.setState({ partners: tree });
				}
			} else {
				notification.error({
					message: Strings.sidebar.partners,
					description: response?.data?.message,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err: unknown) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	onSearch = async (value: string) => {
		await this.getData(value ? true : false);
		this.setState({ withSearch: value ? true : false });
	};

	renderTree() {
		const { partners, withSearch, searchName = "", searchEmail = "" } = this.state;
		const { partners: partnersProp, countries } = this.props;

		return (
			<div className="PartnerTree">
				{!partnersProp && (
					<React.Fragment>
						<div className="PartnerSearch">
							<Search
								placeholder={Strings.generic.searchPartner}
								onChange={(e) => this.setState({ searchName: e.target.value, searchEmail: "" })}
								onSearch={this.onSearch}
								style={{ width: 250 }}
								enterButton
								value={searchName}
							/>
						</div>
						<div className="PartnerEmailSearch">
							<Search
								placeholder={Strings.generic.searchEmail}
								onChange={(e) => this.setState({ searchEmail: e.target.value, searchName: "" })}
								onSearch={this.onSearch}
								style={{ width: 250 }}
								enterButton
								value={searchEmail}
							/>
						</div>
					</React.Fragment>
				)}
				<Tree
					data={partners}
					nodeSvgShape={{ shape: "rect" }}
					pathFunc="step"
					separation={{ siblings: 2, nonSiblings: 2 }}
					orientation="vertical"
					translate={{ x: (document.querySelector(".PartnerTree")?.scrollWidth || window.innerWidth - 220) / 2, y: 100 }}
					allowForeignObjects={true}
					nodeLabelComponent={{
						render: <NodeLabel className="PartnerNode" partners={partnersProp} searched={withSearch} countries={countries} />,
						foreignObjectWrapper: {
							width: 250,
							height: 120,
							y: -55,
							x: -125
						}
					}}
					initialDepth={0.01}
					scaleExtent={{ min: 0.1, max: 1 }}
				/>
			</div>
		);
	}

	render() {
		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.users.partnerTree}</title>
					<meta name="description" content="Visual representation of partners referral binary tree" />
				</Helmet>
				{this.renderTree()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: { language: string; user: any; countries: CountryType[] }) => ({
	language: state.language,
	user: state.user,
	countries: state.countries
});

export default connect(mapStateToProps)(PartnersTree);
